import { Badge, Flex, Loader, Spoiler, Stack, Text, UnstyledButton } from "@mantine/core";
import { type JSX } from "react";

import type { C360HistoryInteraction } from "../api/interactions";

import { useC360CustomerStore } from "../store/useC360CustomerStore";
import { useC360InteractionStore } from "../store/useC360InteractionStore";
import styles from "./CustomerHistoryPanel.module.css";
import { channelLabel, startLabels, statusLabel } from "./interactionLabels";

/**
 * What the panel shows for the call's C360 lookup. `hidden`: no lookup yet, or it didn't ask for history. `loading`: a lookup
 * is running with no history for its number yet. `results`: earlier interactions to show; looking the same number
 * up again reuses them, so they stay as they are. `empty`: the lookup found none. `failed`: the lookup, or part of the history, didn't
 * load. Only `results` opens the panel; the others are its header alone.
 */
export type CustomerHistoryView =
    | { kind: "hidden" | "loading" | "empty" | "failed" }
    | {
          kind: "results";
          earlier: C360HistoryInteraction[];
          historyComplete: boolean;
      };

export function useCustomerHistoryView(sessionId: string): CustomerHistoryView {
    const customer = useC360CustomerStore((state) => state.sessions[sessionId]);
    // The call in progress is already in the history; the expert needs the earlier ones. One without a summary tells
    // the expert nothing, so it's left out.
    const currentInteractionId = useC360InteractionStore((state) => state.sessions[sessionId]?.interactionId);
    if (!customer?.historyRequested) return { kind: "hidden" };
    const { history } = customer;
    const earlier =
        history?.interactions.filter(
            ({ interactionId, summary }) =>
                interactionId !== currentInteractionId && typeof summary === "string" && summary.trim() !== "",
        ) ?? [];
    if (history && earlier.length > 0) {
        return {
            kind: "results",
            earlier,
            historyComplete: history.historyComplete,
        };
    }
    // History already loaded for this number answers for it, even while it's looked up again or after that fails. With
    // nothing to show, a partial load can't say the customer has no history.
    if (history) return { kind: history.historyComplete ? "empty" : "failed" };
    if (customer.status === "searching") return { kind: "loading" };
    if (customer.status === "failed") return { kind: "failed" };
    return { kind: "empty" };
}

// Two lines of the summary at its size (`sm`, 14px) and line height.
const SUMMARY_LINE_HEIGHT = 1.55;
const SUMMARY_TWO_LINES_PX = Math.ceil(2 * 14 * SUMMARY_LINE_HEIGHT);

interface Props {
    view: CustomerHistoryView;
    expanded: boolean;
    onToggle: () => void;
    /** The expert opened an interaction's full summary; `position` is 1 for the newest shown. */
    onSummaryExpanded: (position: number, interaction: C360HistoryInteraction) => void;
}

/** The customer's latest Customer 360 interactions, across their subscriptions on the partner's channel. */
export function CustomerHistoryPanel({ view, expanded, onToggle, onSummaryExpanded }: Props): JSX.Element {
    if (view.kind !== "results") {
        return (
            <Stack gap={0} h="100%" className={styles.panel}>
                <Flex align="center" gap="xs" px="md" className={styles.header}>
                    {view.kind === "loading" && <Loader size="xs" />}
                    <Text size="sm" fw="var(--mantine-font-weight-heavy)" c="dark.1">
                        {view.kind === "loading"
                            ? "Looking up customer history…"
                            : view.kind === "failed"
                              ? "Customer history unavailable"
                              : "No customer history"}
                    </Text>
                </Flex>
            </Stack>
        );
    }
    return (
        <Stack gap={0} h="100%" className={styles.panel}>
            <UnstyledButton onClick={onToggle} aria-expanded={expanded} className={styles.header}>
                <Flex align="center" justify="space-between" px="md" h="100%">
                    <Text size="sm" fw="var(--mantine-font-weight-heavy)" c="dark.1">
                        Customer history ({view.earlier.length})
                    </Text>
                    <Text size="xs" c="dark.2">
                        {expanded ? "Hide" : "Show"}
                    </Text>
                </Flex>
            </UnstyledButton>
            {expanded && (
                <div className={styles.body}>
                    <Stack gap="xxs">
                        {!view.historyComplete && (
                            <Text size="xs" c="dark.2">
                                Some of this customer&apos;s history couldn&apos;t be loaded.
                            </Text>
                        )}
                        {view.earlier.map((interaction, index) => (
                            <HistoryRow
                                key={interaction.interactionId}
                                interaction={interaction}
                                onExpanded={() => onSummaryExpanded(index + 1, interaction)}
                            />
                        ))}
                    </Stack>
                </div>
            )}
        </Stack>
    );
}

function HistoryRow({
    interaction,
    onExpanded,
}: {
    interaction: C360HistoryInteraction;
    onExpanded: () => void;
}): JSX.Element {
    // When it was leads, since that's what the expert scans for; the channel and the call's length go on the right.
    const details = [
        interaction.channel && channelLabel(interaction.channel),
        formatDuration(interaction.durationSeconds),
    ]
        .filter(Boolean)
        .join(" · ");
    const start = startLabels(interaction.startTime);
    return (
        <Stack gap="xxs" className={styles.row}>
            <Flex justify="space-between" align="center" gap="sm">
                <Flex align="center" gap="xxs" className={styles.lead}>
                    <Text size="xs" fw="var(--mantine-font-weight-heavy)" c="dark.0" title={start?.full}>
                        {start?.short ?? "Unknown time"}
                    </Text>
                    {/* Most interactions are completed, so only the others are tagged, which keeps the tags noticeable. */}
                    {interaction.status && interaction.status !== "completed" && (
                        <Badge
                            variant="outline"
                            size="xs"
                            radius="xl"
                            tt="none"
                            fw="normal"
                            color={statusColor(interaction.status)}
                        >
                            {statusLabel(interaction.status)}
                        </Badge>
                    )}
                </Flex>
                <Text size="xs" c="dark.2" truncate="end" miw={0}>
                    {details}
                </Text>
            </Flex>
            {interaction.summary && (
                // Two lines each, so the list scans quickly; the expert opens the ones they need.
                <Spoiler
                    maxHeight={SUMMARY_TWO_LINES_PX}
                    showLabel="Show more"
                    hideLabel="Show less"
                    transitionDuration={0}
                    onExpandedChange={(open) => {
                        if (open) onExpanded();
                    }}
                    classNames={{ control: styles.summaryToggle }}
                >
                    {/* Summaries can carry the customer's name or number, so session replay doesn't record them. */}
                    <Text size="sm" c="dark.0" lh={SUMMARY_LINE_HEIGHT} className="fs-mask">
                        {interaction.summary}
                    </Text>
                </Spoiler>
            )}
        </Stack>
    );
}

/** A transfer means another team had the customer; an open one may still be in progress. A status C360 adds later is gray. */
function statusColor(status: string): "warning.4" | "primary.3" | "gray" {
    if (status === "transferred") return "warning.4";
    if (status === "in_progress" || status === "open") return "primary.3";
    return "gray";
}

function formatDuration(seconds: number | null): string | undefined {
    if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return undefined;
    const total = Math.round(seconds);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
